import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService, LoginCredentials } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export interface User {
  id: number;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userDisplayName: string;
  role?: 'ADMIN' | 'USER' | 'CLIENT' | 'GUEST';
}

export type ModulePermissionAction = 'view' | 'edit' | 'delete';

export interface ModulePermissionFlags {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export type PermissionsMap = Record<string, ModulePermissionFlags>;

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  permissions: PermissionsMap;
  /** True after first successful fetch of permissions (so sidebar can safely filter by canView). */
  permissionsLoaded: boolean;
  can: (moduleKey: string, action: ModulePermissionAction) => boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const emptyPermissions: PermissionsMap = {};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<PermissionsMap>(emptyPermissions);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const navigate = useNavigate();

  const fetchUser = async (): Promise<void> => {
    try {
      const response = await api.get<User>('/profile');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setUser(null);
    }
  };

  const fetchPermissions = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<PermissionsMap>('/permissions');
      setPermissions(response.data ?? emptyPermissions);
      setPermissionsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions(emptyPermissions);
      setPermissionsLoaded(true);
    }
  }, []);

  const can = useCallback(
    (moduleKey: string, action: ModulePermissionAction): boolean => {
      if (!user) return false;
      const p = permissions[moduleKey];
      if (!p) return false;
      switch (action) {
        case 'view':
          return p.canView;
        case 'edit':
          return p.canEdit;
        case 'delete':
          return p.canDelete;
        default:
          return false;
      }
    },
    [user, permissions],
  );

  useEffect(() => {
    const checkAuth = async () => {
      const token = authService.getToken();
      if (token) {
        try {
          await authService.validateSession();
          setIsAuthenticated(true);
          await fetchUser();
          await fetchPermissions();
          // Validate and fix view preferences on session check (e.g., page refresh)
          try {
            await api.get('/settings/user/view-preferences');
          } catch (error) {
            console.error('Failed to fetch view preferences on session check:', error);
          }
        } catch (error) {
          setIsAuthenticated(false);
          setUser(null);
          setPermissions(emptyPermissions);
          setPermissionsLoaded(false);
          authService.setToken('');
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setPermissions(emptyPermissions);
        setPermissionsLoaded(false);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);
      authService.setToken(response.access_token);
      setIsAuthenticated(true);
      await fetchUser();
      await fetchPermissions();

      // Wait a moment for backend validation to complete, then fetch view preferences
      // The backend validates and sets defaults on login, so we fetch to ensure frontend has latest data
      await new Promise(resolve => setTimeout(resolve, 300)); // Small delay to ensure backend validation completes
      
      try {
        const viewPrefsResponse = await api.get('/settings/user/view-preferences');
        const viewPreferences = viewPrefsResponse.data.data || {};
        
        // Trigger events to refresh sidebar view preferences
        window.dispatchEvent(new CustomEvent('viewPreferencesUpdated'));
        window.dispatchEvent(new CustomEvent('userAuthenticated'));
      } catch (error) {
        console.error('Failed to fetch view preferences on login:', error);
      }
      
      // Navigate to dashboard - sidebar will use preferences when rendering
      navigate('/');
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      setPermissions(emptyPermissions);
      setPermissionsLoaded(false);
      navigate('/login');
    }
  };

  const refreshUser = async () => {
    if (isAuthenticated) {
      await fetchUser();
      await fetchPermissions();
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, permissions, permissionsLoaded, can, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

